import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useEffect, useMemo, useContext, createContext } from 'react';
import { Radio as Radio$1, useRadioState, RadioGroup as RadioGroup$1 } from 'reakit/Radio';
import css from '@styled-system/css';
import styled from 'styled-components';
import { Box } from '../Box/index.js';
import { Stack } from '../Stack/index.js';

/* ======================================= */
const RadioWrapper = styled(Box).withConfig({
  displayName: "Radio__RadioWrapper",
  componentId: "sc-1bare03-0"
})(_ref => {
  let {
    isDisabled
  } = _ref;
  return {
    cursor: isDisabled ? 'not-allowed' : 'pointer'
  };
}, css({
  position: 'relative',
  width: 'fit-content',
  display: 'flex'
}));
const FakeRadio = styled(Box).withConfig({
  displayName: "Radio__FakeRadio",
  componentId: "sc-1bare03-1"
})(css({
  marginRight: '10px',
  minWidth: '20px',
  height: '20px',
  color: 'transparent',
  borderRadius: '50%',
  border: '2px solid',
  borderColor: 'selectors.default.offDefault',
  justifyContent: 'center',
  position: 'relative',
  transitionProperty: 'backgroundColor, borderColor',
  transition: '0.15s ease-in-out all',
  '&::before': {
    content: "''",
    width: '10px',
    height: '10px',
    color: 'inherit',
    borderRadius: 'inherit',
    backgroundColor: 'currentColor',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)'
  }
}));
const HiddenRadio = styled(Radio$1).withConfig({
  displayName: "Radio__HiddenRadio",
  componentId: "sc-1bare03-2"
})(css({
  appearance: 'none',
  position: 'absolute',
  cursor: 'pointer',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
  outline: 'none',
  margin: 0,
  opacity: 0,
  WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
  [`&:focus-visible + ${FakeRadio}`]: {
    boxShadow: 'focus'
  },
  [`&:checked:not(:disabled) + ${FakeRadio}`]: {
    color: 'selectors.default.onDefault',
    borderColor: 'selectors.default.onDefault'
  },
  [`&:hover:not(:disabled) + ${FakeRadio}`]: {
    borderColor: 'selectors.default.offHover'
  },
  [`&:hover:checked + ${FakeRadio}`]: {
    color: 'selectors.default.onHover',
    borderColor: 'selectors.default.onHover'
  },
  [`&:disabled + ${FakeRadio}`]: {
    borderColor: 'selectors.default.offDisabled'
  },
  [`&:checked:disabled + ${FakeRadio}`]: {
    borderColor: 'selectors.default.onDisabled',
    '&::before': {
      backgroundColor: 'selectors.default.onDisabled'
    }
  }
}));
const TextWrapper = styled(Box).withConfig({
  displayName: "Radio__TextWrapper",
  componentId: "sc-1bare03-3"
})(css({
  fontSize: 'fontSize20',
  lineHeight: 'lineHeight20'
}));
/* ======================================= */
const RadioContext = /*#__PURE__*/createContext({});
/**
 * RadioGroup is a required component to group all Radio components.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/radio#usage)
 * - [Props](https://blockparty.endpoint.com/components/radio#props)
 * <br />
 *
 * ---
 *
 * @deprecated RadioGroup will be deprecated in the near future. Please use RadioGroupAlpha instead.
 */
const RadioGroup = props => {
  const {
    children,
    name,
    defaultValue: radioDefaultValue,
    ...rootProps
  } = props;
  const radio = useRadioState({
    state: radioDefaultValue && radioDefaultValue
  });
  useEffect(() => {
    radio.setState(radioDefaultValue);
  }, [radioDefaultValue, radio]);
  const context = useMemo(() => ({
    radio,
    name
  }), [radio, name]);
  return /*#__PURE__*/React__default.createElement(RadioContext.Provider, {
    value: context
  }, /*#__PURE__*/React__default.createElement(RadioGroup$1, _extends({
    as: Stack,
    spacing: "space50"
  }, radio, rootProps), children));
};
/* ======================================= */
/**
 * Radios are used when only one choice may be selected in a series of options.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/radio#usage)
 * - [Props](https://blockparty.endpoint.com/components/radio#props)
 * <br />
 *
 * ---
 *
 * @deprecated Radio will be deprecated in the near future. Please use RadioAlpha instead.
 */
const Radio = _ref2 => {
  let {
    children,
    dataTestId = 'radio',
    value,
    onChange,
    isDisabled,
    id,
    className,
    name: radioName,
    ...props
  } = _ref2;
  const {
    radio,
    name
  } = useContext(RadioContext);
  return /*#__PURE__*/React__default.createElement(RadioWrapper, _extends({
    as: "label",
    disabled: isDisabled,
    isDisabled: isDisabled
  }, props), /*#__PURE__*/React__default.createElement(HiddenRadio, _extends({}, radio, {
    "data-test-id": dataTestId,
    disabled: isDisabled,
    id: id,
    name: radioName || name,
    value: value,
    onChange: onChange
  })), /*#__PURE__*/React__default.createElement(FakeRadio, {
    transition: "fast"
  }), /*#__PURE__*/React__default.createElement(TextWrapper, {
    as: "span",
    color: isDisabled ? 'inputDisabled' : 'carbon900'
  }, children));
};

export { Radio, RadioContext, RadioGroup };
